import { memo, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Bot, User, Mic, Volume2, BarChart3 } from "lucide-react";
// import { Wrench } from "lucide-react"; // Re-enable when showing tool call badges
import { cn } from "../lib/utils";
import type { ChatMessage as ChatMessageType } from "../hooks/useSSEChat";
import { QuickFactsCard } from "./QuickFactsCard";
import { ChatFeedback } from "./ChatFeedback";

// ---------------------------------------------------------------------------
// Markdown detection — skip ReactMarkdown for plain text responses
// ---------------------------------------------------------------------------

const MARKDOWN_SYNTAX_RE =
  /(?:^|\n)#{1,6}\s|(?:^|\n)\s*[-*+]\s|(?:^|\n)\s*\d+\.\s|\*\*.+?\*\*|\*.+?\*|`.+?`|~~.+?~~|\[.+?\]\(.+?\)|!\[|(?:^|\n)\|.+?\||(?:^|\n)>\s|(?:^|\n)```|(?:^|\n)---/;

function hasMarkdownSyntax(text: string): boolean {
  return MARKDOWN_SYNTAX_RE.test(text);
}

// ---------------------------------------------------------------------------
// Typing indicator dots
// ---------------------------------------------------------------------------

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1" aria-label="Typing">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-text-muted"
          style={{
            animation: "copilot-bounce 1.2s ease-in-out infinite",
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
    </span>
  );
}

// ---------------------------------------------------------------------------
// ChatMessage component
// ---------------------------------------------------------------------------

interface ChatMessageProps {
  message: ChatMessageType;
  onFeedbackSubmitted?: (messageId: string, feedback: number) => void;
}

export const ChatMessage = memo(function ChatMessage({
  message,
  onFeedbackSubmitted,
}: ChatMessageProps) {
  // Quick Facts inline card
  if (message.role === "quick_facts" && message.quickFactsData) {
    return (
      <div className="flex gap-2 px-4 py-2" data-testid="quick-facts-bubble">
        {/* Avatar */}
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
          <BarChart3 className="h-3.5 w-3.5" />
        </div>
        {/* Card */}
        <div className="min-w-0 flex-1">
          <QuickFactsCard data={message.quickFactsData} compact />
        </div>
      </div>
    );
  }

  const isUser = message.role === "user";
  const isEmpty = !message.content && message.isStreaming;
  const showFeedback =
    message.role === "assistant" &&
    !message.isStreaming &&
    message.logId;
  // Tool call badges hidden for now — uncomment to re-enable
  // const showToolCalls =
  //   message.role === "assistant" &&
  //   !message.isStreaming &&
  //   message.toolCalls &&
  //   message.toolCalls.length > 0;
  const needsMarkdown = useMemo(
    () => !isUser && !!message.content && hasMarkdownSyntax(message.content),
    [isUser, message.content],
  );

  return (
    <div
      className={cn(
        "flex gap-2 px-4 py-2",
        isUser ? "flex-row-reverse" : "flex-row"
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
          isUser ? "bg-brand text-text-inverse" : "bg-surface-inset text-text-muted"
        )}
      >
        {isUser ? (
          <User className="h-3.5 w-3.5" />
        ) : (
          <Bot className="h-3.5 w-3.5" />
        )}
      </div>

      {/* Bubble */}
      <div
        className={cn(
          "flex min-w-0 flex-1 flex-col gap-0.5",
          isUser ? "items-end" : "items-start"
        )}
      >
        <div
          className={cn(
            "max-w-[min(80%,32rem)] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed",
            isUser
              ? "rounded-br-md bg-brand text-text-inverse"
              : "rounded-bl-md bg-surface-inset text-text-primary"
          )}
        >
          {isEmpty ? (
            <TypingDots />
          ) : isUser ? (
            <p className="whitespace-pre-wrap break-words">{message.content}</p>
          ) : needsMarkdown ? (
            <div className="copilot-markdown prose prose-sm max-w-none break-words">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {message.content}
              </ReactMarkdown>
              {message.isStreaming && (
                <span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-text-muted align-text-bottom" />
              )}
            </div>
          ) : (
            <p className="whitespace-pre-wrap break-words">
              {message.content}
              {message.isStreaming && (
                <span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-text-muted align-text-bottom" />
              )}
            </p>
          )}
        </div>
        {/* Tool call indicators — hidden for now (re-enable by uncommenting)
        {showToolCalls && (
          <div className="flex flex-wrap items-center gap-1 px-1 pt-0.5">
            <Wrench className="h-3 w-3 text-text-muted/60" />
            {message.toolCalls!.map((tc, i) => (
              <span
                key={`${tc.tool_name}-${i}`}
                className="inline-flex items-center rounded-full bg-surface-inset/60 px-2 py-0.5 text-[10px] leading-tight text-text-muted"
              >
                {tc.tool_name.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        )}
        */}
        {/* Voice/speaker indicators */}
        {(message.source === "voice" || message.spokenAloud) && (
          <div
            className={cn(
              "flex items-center gap-1 px-1 text-text-muted",
              isUser ? "justify-end" : "justify-start"
            )}
          >
            {message.source === "voice" && (
              <Mic className="h-3 w-3" aria-label="Sent via voice" />
            )}
            {message.spokenAloud && (
              <Volume2 className="h-3 w-3" aria-label="Spoken aloud" />
            )}
          </div>
        )}
        {/* Feedback thumbs for assistant messages */}
        {showFeedback && (
          <ChatFeedback
            logId={message.logId!}
            feedbackState={message.feedbackState}
            onFeedbackSubmitted={(feedback) =>
              onFeedbackSubmitted?.(message.id, feedback)
            }
          />
        )}
      </div>
    </div>
  );
});
